import { useState } from 'react';
import { Activity, RefreshCw, Circle } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { cn } from '@/lib/utils';
import { useUptime } from '@/hooks/useUptime';
import type { UptimeStatus, UptimeSlot, UptimeSystem } from '@/api/endpoints/uptime';

// Aparência por status (barra + pílula). Cores do design system: tertiary (verde),
// amber (laranja/instável), error (vermelho/down), border (cinza/sem dado).
const STATUS_META: Record<UptimeStatus, { bar: string; dot: string; pill: string; label: string }> =
  {
    up: {
      bar: 'bg-tertiary',
      dot: 'text-tertiary',
      pill: 'bg-tertiary/10 text-tertiary',
      label: 'Operacional',
    },
    degraded: {
      bar: 'bg-amber-500',
      dot: 'text-amber-500',
      pill: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      label: 'Instável',
    },
    down: {
      bar: 'bg-error',
      dot: 'text-error',
      pill: 'bg-error/10 text-error',
      label: 'Fora do ar',
    },
    paused: {
      bar: 'bg-border',
      dot: 'text-text-tertiary',
      pill: 'bg-muted text-text-tertiary',
      label: 'Pausado',
    },
    unknown: {
      bar: 'bg-border',
      dot: 'text-text-tertiary',
      pill: 'bg-muted text-text-tertiary',
      label: 'Sem dados',
    },
  };

function clock(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function sinceLabel(ts: number | null): string {
  if (!ts) return 'sem incidentes';
  const diffMin = Math.floor(Date.now() / 1000 - ts) / 60;
  if (diffMin < 60) return `há ${Math.max(1, Math.floor(diffMin))} min`;
  const h = Math.floor(diffMin / 60);
  return `há ${h}h`;
}

// Barra de heartbeat: um traço por fatia da janela. Ao passar o mouse numa fatia,
// mostra um tooltip flutuante com o horário + estado; a fatia sob o cursor ganha
// realce (anel + brilho).
function StatusBar({ slots }: { slots: UptimeSlot[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const active = hover != null ? slots[hover] : null;
  const activeMeta = active ? STATUS_META[active.status] : null;

  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      {/* Tooltip */}
      {active && activeMeta && (
        <div
          className="pointer-events-none absolute bottom-full z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-border bg-bg-elevated px-2.5 py-1.5 shadow-elevated"
          style={{ left: `${((hover! + 0.5) / slots.length) * 100}%` }}
        >
          <div className="flex items-center gap-1.5">
            <span className={cn('size-2 rounded-full', activeMeta.bar)} />
            <span className="text-xs font-semibold text-text-title">{activeMeta.label}</span>
          </div>
          <div className="mt-0.5 text-[11px] tabular-nums text-text-tertiary">
            {clock(active.at)}
          </div>
        </div>
      )}

      <div
        className="flex h-8 items-stretch gap-[3px]"
        role="img"
        aria-label="Histórico das últimas 5 horas"
      >
        {slots.map((s, i) => {
          const meta = STATUS_META[s.status];
          const isActive = hover === i;
          return (
            <span
              key={i}
              onMouseEnter={() => setHover(i)}
              className={cn(
                'flex-1 cursor-pointer rounded-full transition-all',
                meta.bar,
                isActive ? 'ring-2 ring-text-title/30 brightness-110' : 'hover:brightness-110'
              )}
            />
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: UptimeStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
        meta.pill
      )}
    >
      <Circle size={8} fill="currentColor" className={meta.dot} />
      {meta.label}
    </span>
  );
}

function SystemCard({ system }: { system: UptimeSystem }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-text-title">{system.name}</span>
            <StatusPill status={system.status} />
          </div>
          {system.url && <p className="mt-0.5 truncate text-xs text-text-tertiary">{system.url}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-4 text-right">
          <div>
            <p className="text-sm font-semibold tabular-nums text-text-title">
              {system.uptimePct.toFixed(1)}%
            </p>
            <p className="text-[11px] text-text-tertiary">disponibilidade</p>
          </div>
          <div>
            <p className="text-sm font-semibold tabular-nums text-text-title">
              {system.avgResponseMs != null ? `${system.avgResponseMs} ms` : '—'}
            </p>
            <p className="text-[11px] text-text-tertiary">latência méd.</p>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <StatusBar slots={system.slots} />
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-text-tertiary">
          <span>5h atrás</span>
          <span className={cn(system.lastIncidentAt ? 'text-error' : 'text-text-tertiary')}>
            {system.lastIncidentAt
              ? `último incidente ${sinceLabel(system.lastIncidentAt)}`
              : 'sem incidentes'}
          </span>
          <span>agora</span>
        </div>
      </div>
    </div>
  );
}

function Legend() {
  const items: { status: UptimeStatus }[] = [
    { status: 'up' },
    { status: 'degraded' },
    { status: 'down' },
    { status: 'unknown' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((it) => {
        const meta = STATUS_META[it.status];
        return (
          <span key={it.status} className="flex items-center gap-1.5 text-xs text-text-secondary">
            <span className={cn('size-2.5 rounded-full', meta.bar)} />
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

export function Uptime() {
  const { data, isLoading, isFetching, refetch } = useUptime();
  const systems = data?.systems ?? [];
  const operational = systems.filter((s) => s.status === 'up').length;
  const anyDown = systems.some((s) => s.status === 'down');
  const anyDegraded = systems.some((s) => s.status === 'degraded');

  const overall = anyDown
    ? { text: 'Instabilidade detectada', cls: 'text-error' }
    : anyDegraded
      ? { text: 'Alguns sistemas instáveis', cls: 'text-amber-600 dark:text-amber-400' }
      : { text: 'Todos os sistemas operacionais', cls: 'text-tertiary' };

  return (
    <Layout breadcrumbs={[{ label: 'Monitoramento' }, { label: 'Uptime' }]}>
      <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="max-md:hidden flex items-center gap-2 text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
              <Activity className="text-tertiary" size={26} />
              Uptime
            </h1>
            <p className="text-sm text-text-secondary">
              Disponibilidade dos sistemas nas últimas 5 horas
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
          >
            <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
            Atualizar
          </button>
        </div>

        {/* Resumo geral */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-10 items-center justify-center rounded-full',
                anyDown ? 'bg-error/10' : anyDegraded ? 'bg-amber-500/10' : 'bg-tertiary/10'
              )}
            >
              <Activity className={overall.cls} size={20} />
            </span>
            <div>
              <p className={cn('text-sm font-semibold', overall.cls)}>{overall.text}</p>
              <p className="text-xs text-text-tertiary">
                {operational} de {systems.length} operacionais
                {data && !data.configured && ' · UptimeRobot não configurado'}
              </p>
            </div>
          </div>
          <Legend />
        </div>

        {/* Lista de sistemas */}
        {isLoading ? (
          <div className="grid gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-card" />
            ))}
          </div>
        ) : systems.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
            <p className="text-sm font-semibold text-text-title">Nenhum sistema monitorado</p>
            <p className="mt-1 text-xs text-text-tertiary">
              Configure os monitores na UptimeRobot para ver o status aqui.
            </p>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {systems.map((s) => (
              <SystemCard key={s.key} system={s} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
